import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { WarningOctagon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

// Owner full account delete (the only account deletion in the console), two steps: start -> confirm token; confirm needs DELETE typed plus the token.
export default function DeleteUserDialog({ open, onOpenChange, user, firstName }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(null);
  const [typed, setTyped] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const reset = () => { setStep(null); setTyped(""); setToken(""); setError(null); };
  const ok = step && typed.trim() === "DELETE" && token.trim() === step.confirm_token;

  const run = async (fn) => { setBusy(true); setError(null); try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(false); } };
  const start = () => run(async () => setStep(await api(`/admin/users/${user.id}/delete/start`, { method: "POST" })));
  const confirm = () => run(async () => {
    const r = await api(`/admin/users/${user.id}/delete/confirm`, { body: { confirm_token: token.trim() } });
    toast.success(`Account deleted. ${r.bans_kept_hashed} bans and ${r.upheld_reports_kept} upheld reports kept as anonymous hashes.`);
    onOpenChange(false);
    navigate("/admin/users", { replace: true });
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) { reset(); onOpenChange(o); } }}>
      <DialogContent className="rounded-[20px] sm:max-w-lg" data-testid="user-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><WarningOctagon size={20} className="cw-error" />Delete {firstName ? `${firstName}’s account` : "this account"} permanently?</DialogTitle>
          <DialogDescription className="leading-6">This permanently erases their profile, photos, selfies, messages they sent, matches, grants, notes and push history. It can’t be undone. Banned identifiers are kept as anonymous hashes, so a banned person still can’t sign up again. Upheld reports are kept, anonymized. Purchase records stay, de-identified.</DialogDescription>
        </DialogHeader>
        {!step ? (
          <p className="cw-soft text-sm">Step 1 creates a one-time confirmation code that only works for you, for a few minutes.</p>
        ) : (
          <div className="space-y-4" data-testid="user-delete-step2">
            <ul className="grid grid-cols-2 gap-2 rounded-xl bg-[var(--cw-surface-tertiary)] p-4 text-sm tabular-nums" data-testid="user-delete-summary">
              <li>{step.summary.photos} photos</li><li>{step.summary.messages_sent} messages sent</li>
              <li>{step.summary.active_bans} active bans (kept, hashed)</li><li>{step.summary.upheld_reports} upheld reports (kept)</li>
            </ul>
            <div className="rounded-xl border border-border p-3"><p className="cw-muted text-xs">Confirmation code · expires in {Math.round(step.expires_in_seconds / 60)} min</p>
              <p className="mt-1 select-all break-all font-mono text-sm" data-testid="user-delete-token">{step.confirm_token}</p></div>
            <div><Label htmlFor="del-typed">Type DELETE to confirm</Label><Input id="del-typed" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" className="mt-2 font-mono" autoComplete="off" data-testid="user-delete-typed-input" /></div>
            <div><Label htmlFor="del-token">Type or paste the confirmation code</Label><Input id="del-token" value={token} onChange={(e) => setToken(e.target.value)} className="mt-2 font-mono" autoComplete="off" data-testid="user-delete-token-input" /></div>
          </div>
        )}
        {error && <p className="cw-error text-sm" role="alert" data-testid="user-delete-error">{error}</p>}
        <DialogFooter className="gap-2">
          <Button variant="ghost" disabled={busy} onClick={() => { reset(); onOpenChange(false); }} data-testid="user-delete-cancel-button">Cancel</Button>
          {!step ? <Button variant="destructive" onClick={start} disabled={busy} data-testid="user-delete-start-button">{busy ? "Working…" : "Step 1: Get confirmation code"}</Button>
            : <Button variant="destructive" onClick={confirm} disabled={!ok || busy} data-testid="user-delete-confirm-button">{busy ? "Deleting…" : "Step 2: Delete account permanently"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
